/**
 * Recurrence utilities for event expansion.
 *
 * These are pure functions with no external dependencies so they can be
 * tested in isolation and imported by EventsPage.js.
 *
 * Recurrence rule shape (stored on event.recurrence):
 * {
 *   type: 'none' | 'weekly' | 'monthly',
 *   dayOfWeek: 0-6   (weekly only, 0=Sun)
 *   dayOfMonth: 1-31  (monthly only)
 *   endDate: Date | null
 * }
 *
 * Backward compatibility:
 *   - If event.recurrence is absent/undefined → treated as { type: 'none' }
 *   - If event.location is absent/undefined  → treated as { useProvider: true }
 */

/**
 * Expands a single event into occurrences within [rangeStart, rangeEnd].
 * Non-recurring events are returned unchanged in a single-element array.
 *
 * @param {Object} event
 * @param {Date}   rangeStart  inclusive
 * @param {Date}   rangeEnd    inclusive
 * @returns {Object[]}
 */
export function expandEvent(event, rangeStart, rangeEnd) {
  const rType = event.recurrence?.type || 'none';
  if (rType === 'none') return [event];

  const duration = new Date(event.end) - new Date(event.start);
  const rEnd = event.recurrence?.endDate
    ? new Date(event.recurrence.endDate)
    : rangeEnd;
  const bound = rEnd < rangeEnd ? rEnd : rangeEnd;

  const occurrences = [];
  let cur = new Date(event.start);

  while (cur <= bound) {
    if (cur >= rangeStart) {
      occurrences.push({
        ...event,
        _id: `${event._id}_${cur.getTime()}`,
        start: new Date(cur),
        end: new Date(cur.getTime() + duration),
      });
    }
    if (rType === 'weekly') {
      cur = new Date(cur);
      cur.setDate(cur.getDate() + 7);
    } else if (rType === 'monthly') {
      cur = new Date(cur);
      cur.setMonth(cur.getMonth() + 1);
    } else {
      break; // unknown type — prevent infinite loop
    }
  }
  return occurrences;
}

/**
 * Returns the next occurrence of a recurring event at or after `from`.
 * Returns the original event for non-recurring events.
 * Returns null when all occurrences are exhausted (past endDate).
 *
 * @param {Object} event
 * @param {Date}   from  default: now
 * @returns {Object|null}
 */
export function nextOccurrence(event, from = new Date()) {
  const rType = event.recurrence?.type || 'none';
  if (rType === 'none') return event;

  const duration = new Date(event.end) - new Date(event.start);
  let cur = new Date(event.start);

  while (cur < from) {
    if (rType === 'weekly') {
      cur = new Date(cur);
      cur.setDate(cur.getDate() + 7);
    } else if (rType === 'monthly') {
      cur = new Date(cur);
      cur.setMonth(cur.getMonth() + 1);
    } else {
      break;
    }
    if (event.recurrence?.endDate && cur > new Date(event.recurrence.endDate)) {
      return null;
    }
  }
  return { ...event, start: new Date(cur), end: new Date(cur.getTime() + duration) };
}
